// components/AuthComponents/registration-form.tsx
"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import InitialForm from "./steps/initial-form";
import VerifyEmail from "./steps/verify-email";
import PhoneNumber from "./steps/phone-number";
import VerifyPhone from "./steps/verify-phone";
import Success from "./steps/success";
import SuccessPhoneVerified from "./steps/successPhone";
import type { RegistrationStep, RegistrationData } from "@/types/auth";

interface RegistrationFormProps {
  callbackUrl?: string;
  defaultStep?: RegistrationStep;
}

export default function RegistrationForm({ callbackUrl = "/feed", defaultStep }: RegistrationFormProps) {
  const router = useRouter();
  const [step, setStep] = useState<RegistrationStep>(defaultStep ?? "initial");
  const [data, setData] = useState<RegistrationData>({
    firstName: "",
    lastName: "",
    username: "",
    password: "",
    email: "",
    phoneNumber: "",
    verificationCode: "",
  });
  const [error, setError] = useState("");

  const handleBack = () => {
    setError("");
    switch (step) {
      case "initial":
        router.back();
        break;
      case "verify-email":
        setStep("initial");
        break;
      case "success":
        setStep("verify-email");
        break;
      case "phone-number":
        setStep("success");
        break;
      case "verify-phone":
        setStep("phone-number");
        break;
      case "successPhone":
        setStep("verify-phone");
        break;
    }
  };

  const updateData = (newPartial: Partial<RegistrationData>) =>
    setData((old) => ({ ...old, ...newPartial }));

  return (
    <div className="w-full relative max-w-[26rem] mx-auto sm:max-w-md sm:mx-auto">
      {error && (
        <div className="mb-4 p-3.5 rounded-xl bg-destructive/10 text-destructive text-sm border border-destructive/20">
          {error}
        </div>
      )}

        {step === "initial" && (
          <InitialForm
            data={data}
            onUpdateAction={updateData}
            onBackAction={handleBack}
            onNextAction={() => setStep("verify-email")}
          />
        )}

        {step === "verify-email" && (
          <VerifyEmail
            email={data.email}
            password={data.password}
            firstName={data.firstName}
            lastName={data.lastName}
            username={data.username}
            onBackAction={handleBack}
            onNextAction={() => setStep("success")}
            onErrorAction={(msg) => setError(msg)}
          />
        )}

        {step === "success" && (
          <Success onNextAction={() => setStep("phone-number")} />
        )}

        {step === "phone-number" && (
          <PhoneNumber
            onSubmitAction={(phoneNumber, verificationId) => {
              updateData({ phoneNumber, verificationCode: verificationId });
              setStep("verify-phone");
            }}
            onErrorAction={(msg) => setError(msg)}
            onBackAction={handleBack}
            onSkipAction={() => router.push(callbackUrl)}
          />
        )}

        {step === "verify-phone" && (
          <VerifyPhone
            phoneNumber={data.phoneNumber}
            verificationId={data.verificationCode}
            onSuccessAction={() => setStep("successPhone")}
            onNextAction={() => setStep("successPhone")}
            onErrorAction={(msg) => setError(msg)}
            onBackAction={handleBack}
          />
        )}

        {step === "successPhone" && (
          <SuccessPhoneVerified onNextAction={() => router.push(callbackUrl)} />
        )}
    </div>
  );
}
